// @flow
import { put, takeLatest, call, select, fork, take, cancel } from 'redux-saga/effects';
import {
	resetCheckSession,
	checkSessionSuccess,
	initiateLogin,
	loginSuccess,
	loginError,
	initRequestOTP,
	requestOTPError,
	requestOTPSuccess,
	// logout as logoutAction,
	checkSessionError,
	setMpinSuccess,
	setMpinError,
	resetSession,
	fetchSessionId as fetchSessionIdAction,
	fetchSessionIdSuccess,
	cancelFetchSessionId,
} from '../actions/auth';
import {
	LOGIN,
	LOGOUT,
	CHECK_SESSION,
	REQUEST_OTP,
	SETTING_MPIN,
	VALIDATE_OTP,
	TOKEN_AUTHENTICATE,
	FETCH_SESSION_ID,
	UPDATE_SESSION_DATA,
	CANCEL_FETCH_SESSION_ID,
} from '../actions/types';
import { setCookie, deleteUserCookies } from '../utils';
import {
	requestOtpLogin,
	login as loginAPI,
	chkSessionValidity,
	setMpin as setMpinAPI,
	validateOTP as validateOTPAPI,
	tokenAuthentication as tokenAuthenticationAPI,
	fetchSessionId as fetchSessionIdAPI,
	logout as logoutAPI,
	updateSessionData as updateSessionDataAPI,
} from '../services/auth';
import { showSuccessMsg, showErrorMsg } from '../actions/snackbar';
import { setUser, resetUser } from '../actions/user';
import { saveCartSuccess, clearLastTransaction, saveCart, updateCartSuccess } from '../actions/cart';
import { fetchCart as fetchCartAPI } from '../services/cart';
import { selectPod } from '../actions/pods';
import { VSHELF_MACHINE_SKU } from '../constants';
import { updateSkuPurchareCount } from '../actions/skus';
import { updateVSkuPurchareCount } from '../actions/virtual-shelf';

function* login(action) {
	try {
		yield put(initiateLogin());
		const { mobile, mpin, sessionId: reqSessionId } = action.payload;
		if (!mobile || !mpin) {
			throw new Error('Required fields missing');
		}
		const { sessionId } = yield call(loginAPI, mobile, mpin, reqSessionId);

		setCookie('user', JSON.stringify({ mobile }));
		setCookie('sessionId', sessionId);
		// setCookie('cart', JSON.stringify(updatedCart));
		yield put(setUser({ mobile }));
		yield put(loginSuccess(sessionId));
	} catch (error) {
		yield put(loginError(error));
		yield put(showErrorMsg(error.message));
	}
}

export function* logout(action: {[key: string]: any}): Iterable<any> {
	try {
		deleteUserCookies();
		yield put(resetUser());
		yield put(resetSession());
		yield call(logoutAPI, action.payload);
		yield put(fetchSessionIdAction(''));
	} catch (error) {}
}

function* checkSession(action) {
	try {
		yield put(resetCheckSession());
		yield call(chkSessionValidity, action.payload);
		yield put(checkSessionSuccess());
	} catch (error) {
		// console.error(error);
		yield put(checkSessionError());
		yield call(logout);
	}
}

function* requestOTP(action) {
	try {
		yield put(initRequestOTP());
		const { mobile, type } = action.payload;
		yield call(requestOtpLogin, mobile, type);
		yield put(requestOTPSuccess());
		yield put(showSuccessMsg('OTP sent successfully'));
	} catch (error) {
		yield put(requestOTPError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* setMpin(action) {
	try {
		yield call(setMpinAPI, action.payload);
		yield put(setMpinSuccess());
		yield put(showSuccessMsg('Mpin set successfully'));
	} catch (error) {
		yield put(setMpinError(error));
		yield put(showErrorMsg('Mpin not set successfully. Please try again'));
	}
}

function* validateOTP(action) {
	try {
		yield put(initiateLogin());
		const { mobile, otp, type, sessionId: reqSessionId } = action.payload;
		if (!mobile || !otp) {
			throw new Error('Required fields missing');
		}

		yield put(cancelFetchSessionId());
		const { sessionId } = yield call(validateOTPAPI, mobile, otp, type, reqSessionId);
		const { lastTransaction } = yield select((state) => state.cart || {});

		if (lastTransaction) {
			yield put(updateCartSuccess(lastTransaction));
			yield put(clearLastTransaction());
			const { skuData: { isVirtualSku = false, action } = {} } = lastTransaction;
			if (isVirtualSku) {
				yield put(updateVSkuPurchareCount(lastTransaction));
				if (action === VSHELF_MACHINE_SKU) {
					yield put(updateSkuPurchareCount(lastTransaction));
				}
			} else {
				yield put(updateSkuPurchareCount(lastTransaction));
				if (action === VSHELF_MACHINE_SKU) {
					yield put(updateVSkuPurchareCount(lastTransaction));
				}
			}
			yield put(saveCart(mobile, sessionId, 'PRODUCTS'));
		} else {
			// fetch user pending cart
			const cartData = yield call(fetchCartAPI, { cart: {}, mobile, sessionId });
			const updatedCart = {
				...cartData,
				message: '',
				fetched: true,
				cartError: false,
			};
			yield put(saveCartSuccess(updatedCart));
		}

		yield put(setUser({ mobile }));
		yield put(loginSuccess(sessionId));
		setCookie('user', JSON.stringify({ mobile }));
		setCookie('sessionId', sessionId);
		// setCookie('cart', JSON.stringify(updatedCart));
	} catch (error) {
		yield put(loginError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* tokenAuthentication(action) {
	try {
		const {token, sessionId: reqSessionId} = action.payload;
		if (!token) {
			throw new Error('Token missing');
		}

		yield put(cancelFetchSessionId());		
		const { sessionId, user: mobile, imei: vm_imei_id } = yield call(tokenAuthenticationAPI, token, reqSessionId);

		// fetch user pending cart
		const cartData = yield call(fetchCartAPI, { cart: {}, mobile, sessionId });
		const updatedCart = {
			...cartData,
			message: '',
			fetched: true,
			cartError: false,
		};
		yield put(saveCartSuccess(updatedCart));
		setCookie('user', JSON.stringify({ mobile }));
		setCookie('sessionId', sessionId);
		setCookie('cart', JSON.stringify(updatedCart));
		yield put(selectPod({ vm_imei_id }));
		yield put(setUser({ mobile }));
		yield put(loginSuccess(sessionId));
	} catch (error) {
		yield put(loginError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* fetchSessionId(action) {
	try {
		const { session: newSessionId, logged: loggedIn = false } = yield call(fetchSessionIdAPI, action.payload);
		const { mobile } = yield select((state) => state.user || {});
		const { sessionId } = yield select((state) => state.auth || {});

		if (mobile && !loggedIn) {
			yield put(resetUser());
			deleteUserCookies();
		}

		if (newSessionId !== sessionId) {
			setCookie('sessionId', newSessionId);
			yield put(fetchSessionIdSuccess(newSessionId));
		}
	} catch (error) {
		yield put(showErrorMsg(error.message));
	}
}

function* forkFetchSessionId(action) {
	const genFetchSessionId = yield fork(fetchSessionId, action);
	yield take(CANCEL_FETCH_SESSION_ID);
	yield cancel(genFetchSessionId);
}

function* updateSessionData(action) {
	try {
		const { data = {}, sessionId } = action.payload;
		yield call(updateSessionDataAPI, data, sessionId);
	} catch (error) {}
}

export function* watchAuth(): Iterable<any> {
	yield takeLatest(LOGIN, login);
	yield takeLatest(LOGOUT, logout);
	yield takeLatest(CHECK_SESSION, checkSession);
	yield takeLatest(REQUEST_OTP, requestOTP);
	yield takeLatest(SETTING_MPIN, setMpin);
	yield takeLatest(VALIDATE_OTP, validateOTP);
	yield takeLatest(TOKEN_AUTHENTICATE, tokenAuthentication);
	yield takeLatest(FETCH_SESSION_ID, forkFetchSessionId);
	yield takeLatest(UPDATE_SESSION_DATA, updateSessionData);
}
